import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Plus, Trash2, ShieldCheck, Sparkles, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RupiahInput } from "@/components/RupiahInput";
import { useApp } from "@/context/AppContext";
import { EMPTY_DATA, SAMPLE_DATA } from "@/lib/sampleData";
import { formatCompact } from "@/lib/currency";
import { track, trackCekSelesai } from "@/lib/analytics";

const TOTAL_STEPS = 4;

export default function Assessment() {
  const navigate = useNavigate();
  const { saveAssessment, generateReport } = useApp();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({ ...EMPTY_DATA });
  const cekSelesaiSent = useRef(false); // Meta Pixel: maksimal 1 event per penyelesaian

  useEffect(() => {
    track("assessment_started");
  }, []);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const loadSample = () => setData({ ...SAMPLE_DATA, sideIncomes: [...SAMPLE_DATA.sideIncomes] });

  const addSide = () =>
    set({
      sideIncomes: [
        ...data.sideIncomes,
        { id: `s_${Date.now()}`, name: "", amount: 0 },
      ],
    });
  const updateSide = (id, patch) =>
    set({
      sideIncomes: data.sideIncomes.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    });
  const removeSide = (id) =>
    set({ sideIncomes: data.sideIncomes.filter((s) => s.id !== id) });

  const canContinue =
    step === 1
      ? data.familyName.trim().length > 0
      : step === 2
        ? // Jangan pernah lanjut dengan pendapatan kosong total — assessment
          // tanpa pemasukan & pengeluaran tidak boleh menghasilkan skor.
          (Number(data.husbandSalary) || 0) + (Number(data.wifeSalary) || 0) > 0
        : true;

  const next = () => {
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
      window.scrollTo(0, 0);
    } else {
      saveAssessment(data);
      const rpt = generateReport(data);
      if (!cekSelesaiSent.current) {
        cekSelesaiSent.current = true;
        trackCekSelesai(); // Meta Pixel: assessment benar-benar selesai & hasil dibuat
      }
      track("assessment_completed", { status: rpt?.base?.status });
      navigate("/result");
    }
  };

  const back = () => {
    if (step > 1) setStep((s) => s - 1);
    else navigate("/");
  };

  const progress = (step / TOTAL_STEPS) * 100;

  return (
    <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-2xl bg-white">
      {/* Header + progress */}
      <header className="sticky top-0 z-10 bg-white/90 px-6 pb-4 pt-6 backdrop-blur-lg">
        <div className="flex items-center justify-between">
          <button onClick={back} data-testid="assessment-back" className="text-zinc-500">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <span className="text-xs font-semibold text-zinc-400 font-heading">
            Langkah {step} dari {TOTAL_STEPS}
          </span>
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
          </div>
        </div>
        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
          <motion.div
            className="h-full rounded-full bg-emerald-500"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </header>

      <div className="px-6 pb-40 pt-6">
        {/* Privacy reassurance — visible BEFORE any financial input */}
        <div
          data-testid="privacy-reassurance"
          className="mb-6 rounded-2xl border border-emerald-100 bg-emerald-50 p-4"
        >
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-emerald-600" />
            <p className="font-heading text-sm font-bold text-emerald-800">
              Data keuangan Anda tetap pribadi 🔒
            </p>
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-emerald-700">
            Angka yang Anda masukkan, seperti penghasilan, tabungan, cicilan, utang, dan
            pengeluaran, hanya diproses di HP atau komputer Anda dan tidak dikirim ke server
            kami. Kami tidak dapat melihat angka-angka keuangan yang Anda masukkan.
          </p>
        </div>

        {/* HARDENING: mount tidak pernah menunggu animasi.
            Step baru di-remount sinkron oleh React (key={step}); transisi hanyalah
            animasi CSS dekoratif (kebal terhadap rAF yang berhenti di tab background)
            dan dinonaktifkan sepenuhnya oleh prefers-reduced-motion (lihat index.css). */}
        <div key={step} className="step-enter">
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h1 className="font-heading text-2xl font-extrabold tracking-tight text-zinc-900">
                    Tentang Keluarga Anda
                  </h1>
                  <button
                    data-testid="assessment-sample-btn"
                    onClick={loadSample}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Coba Data Contoh
                  </button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-700 font-heading">
                    Nama keluarga
                  </Label>
                  <Input
                    data-testid="input-family-name"
                    value={data.familyName}
                    onChange={(e) => set({ familyName: e.target.value })}
                    placeholder="Keluarga Wijaya"
                    className="h-12 rounded-2xl border-zinc-200 text-base"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-zinc-700 font-heading">
                      Nama Suami <span className="text-zinc-400">(opsional)</span>
                    </Label>
                    <Input
                      data-testid="input-husband-name"
                      value={data.husbandName}
                      onChange={(e) => set({ husbandName: e.target.value })}
                      placeholder="Suami"
                      className="h-12 rounded-2xl border-zinc-200"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-zinc-700 font-heading">
                      Nama Istri <span className="text-zinc-400">(opsional)</span>
                    </Label>
                    <Input
                      data-testid="input-wife-name"
                      value={data.wifeName}
                      onChange={(e) => set({ wifeName: e.target.value })}
                      placeholder="Istri"
                      className="h-12 rounded-2xl border-zinc-200"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h1 className="font-heading text-2xl font-extrabold tracking-tight text-zinc-900">
                    Pendapatan Utama
                  </h1>
                  <p className="mt-2 text-sm text-zinc-500">
                    Pisahkan pendapatan utama dari penghasilan tambahan agar stress
                    test lebih akurat.
                  </p>
                </div>
                <RupiahInput
                  label="Gaji Utama Suami"
                  testId="input-husband-salary"
                  value={data.husbandSalary}
                  onChange={(v) => set({ husbandSalary: v })}
                />
                <RupiahInput
                  label="Gaji Utama Istri"
                  testId="input-wife-salary"
                  value={data.wifeSalary}
                  onChange={(v) => set({ wifeSalary: v })}
                />
                <div className="rounded-2xl bg-zinc-50 p-4 text-sm text-zinc-500">
                  Pendapatan utama:{" "}
                  <span className="font-mono font-semibold text-zinc-800">
                    {formatCompact((Number(data.husbandSalary) || 0) + (Number(data.wifeSalary) || 0))}
                  </span>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h1 className="font-heading text-2xl font-extrabold tracking-tight text-zinc-900">
                    Penghasilan Tambahan
                  </h1>
                  <p className="mt-2 text-sm text-zinc-500">
                    Tambahkan sumber penghasilan tambahan seperti freelance, usaha
                    sampingan, komisi, atau rental.
                  </p>
                </div>

                {data.sideIncomes.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-zinc-200 p-6 text-center text-sm text-zinc-400">
                    Tidak ada penghasilan tambahan
                  </div>
                )}

                <div className="space-y-4">
                  {data.sideIncomes.map((s) => (
                    <div key={s.id} className="rounded-2xl border border-zinc-200 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <Label className="text-sm font-medium text-zinc-700 font-heading">
                          Sumber penghasilan
                        </Label>
                        <button
                          data-testid={`side-remove-${s.id}`}
                          onClick={() => removeSide(s.id)}
                          className="text-zinc-400 hover:text-rose-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <Input
                        data-testid={`side-name-${s.id}`}
                        value={s.name}
                        onChange={(e) => updateSide(s.id, { name: e.target.value })}
                        placeholder="Nama sumber (mis. Freelance)"
                        className="mb-3 h-11 rounded-xl border-zinc-200"
                      />
                      <RupiahInput
                        label="Pendapatan rata-rata / bulan"
                        testId={`side-amount-${s.id}`}
                        value={s.amount}
                        onChange={(v) => updateSide(s.id, { amount: v })}
                      />
                    </div>
                  ))}
                </div>

                <button
                  data-testid="add-side-income"
                  onClick={addSide}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-zinc-200 py-3.5 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50"
                >
                  <Plus className="h-4 w-4" />
                  Tambah Penghasilan Tambahan
                </button>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-7">
                <div>
                  <h1 className="font-heading text-2xl font-extrabold tracking-tight text-zinc-900">
                    Kewajiban &amp; Pengeluaran
                  </h1>
                </div>

                <div className="space-y-4">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 font-heading">
                    Cicilan
                  </h2>
                  <RupiahInput label="KPR / Cicilan Rumah" testId="input-mortgage" value={data.mortgage} onChange={(v) => set({ mortgage: v })} />
                  <RupiahInput label="Cicilan Kendaraan" testId="input-vehicle" value={data.vehicle} onChange={(v) => set({ vehicle: v })} />
                  <RupiahInput label="Kartu Kredit" testId="input-credit-card" value={data.creditCard} onChange={(v) => set({ creditCard: v })} />
                  <RupiahInput label="Cicilan Lain" testId="input-other-debt" value={data.otherDebt} onChange={(v) => set({ otherDebt: v })} />
                </div>

                <div className="space-y-3">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 font-heading">
                    Pengeluaran Rutin Keluarga
                  </h2>
                  <RupiahInput
                    label="Pengeluaran rutin / bulan"
                    testId="input-living-expenses"
                    value={data.livingExpenses}
                    onChange={(v) => set({ livingExpenses: v })}
                    hint="Makanan, sekolah, listrik, transportasi, dan kebutuhan rutin lainnya. Jangan masukkan cicilan kembali di sini."
                  />
                </div>

                <div className="space-y-3">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 font-heading">
                    Dana Darurat
                  </h2>
                  <RupiahInput
                    label="Dana darurat yang tersedia saat ini"
                    testId="input-emergency-fund"
                    value={data.emergencyFund}
                    onChange={(v) => set({ emergencyFund: v })}
                  />
                </div>
              </div>
            )}
          </div>
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-1/2 w-full max-w-md lg:max-w-2xl -translate-x-1/2 border-t border-zinc-100 bg-white/90 p-4 backdrop-blur-lg">
        <Button
          data-testid="assessment-next"
          disabled={!canContinue}
          onClick={next}
          className="h-14 w-full rounded-full bg-zinc-900 text-base font-semibold hover:bg-zinc-800 disabled:opacity-40"
        >
          {step < TOTAL_STEPS ? "Lanjut" : "Lihat Ketahanan Keluarga"}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
